// @ts-nocheck
import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {fetchReplies, resetReplies} from '../../context/forms/FormsAction';
import Context from '../../context/Context';
import {useParams} from 'react-router-dom';
import {URL} from '../../service/Service';
import Breadcrumb from '../../components/Breadcrumb';
import Spinner from '../../components/Spinner';
import {format, isValid, parse, parseISO} from 'date-fns';
import {
    ArrowDownTrayIcon,
    ArrowPathIcon,
    ChevronDownIcon,
    ClipboardDocumentCheckIcon,
    DocumentDuplicateIcon,
    InboxArrowDownIcon,
    MagnifyingGlassIcon,
    ShoppingBagIcon,
    XMarkIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';

const itemColorPalette = [
    {
        row: 'border-emerald-100 bg-emerald-50/60',
        marker: 'bg-emerald-500',
        quantity: 'bg-emerald-100 !text-emerald-800',
    },
    {
        row: 'border-blue-100 bg-blue-50/60',
        marker: 'bg-blue-500',
        quantity: 'bg-blue-100 !text-blue-800',
    },
    {
        row: 'border-violet-100 bg-violet-50/60',
        marker: 'bg-violet-500',
        quantity: 'bg-violet-100 !text-violet-800',
    },
    {
        row: 'border-amber-100 bg-amber-50/60',
        marker: 'bg-amber-500',
        quantity: 'bg-amber-100 !text-amber-800',
    },
    {
        row: 'border-rose-100 bg-rose-50/60',
        marker: 'bg-rose-500',
        quantity: 'bg-rose-100 !text-rose-800',
    },
];

const Replies = ({adminMode = false}) => {
    const {idform} = useParams();
    const {state, dispatch} = useContext(Context);
    const {loading, error, data} = state.replies;
    const baseUrl = `${URL}/api/forms/${idform}/replies`;

    const [replies, setReplies] = useState([]);
    const [expandedReplyId, setExpandedReplyId] = useState(null);
    const [syncingPayments, setSyncingPayments] = useState(false);
    const [syncReport, setSyncReport] = useState(null);
    const [lastWebhookAt, setLastWebhookAt] = useState(null);
    const [lastReferenceSyncAt, setLastReferenceSyncAt] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [refundReference, setRefundReference] = useState(null);
    const [refundAmount, setRefundAmount] = useState('');
    const [refundReason, setRefundReason] = useState('');
    const [refundIban, setRefundIban] = useState('');
    const [refundBic, setRefundBic] = useState('');
    const [submittingRefund, setSubmittingRefund] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [paidCount, setPaidCount] = useState(0);
    const [pendingCount, setPendingCount] = useState(0);
    const [loadingReplyId, setLoadingReplyId] = useState(null);
    const [statusChange, setStatusChange] = useState(null);
    const [statusChangeReason, setStatusChangeReason] = useState('');
    const [submittingStatus, setSubmittingStatus] = useState(false);

    const referenceStatusOptions = ['PAID', 'UNPAID', 'CANCELLED', 'ERROR', 'EXPIRED', 'REFUNDED'];

    const formsPath = adminMode ? '/admin/forms' : '/forms';
    const dismissedSyncStorageKey = `manual-reference-sync-dismissed:${adminMode ? 'admin' : 'account'}:${idform}`;
    const selectedFormPath = `${formsPath}?form=${encodeURIComponent(idform)}`;
    const pages = [
        {name: 'Formulários', href: selectedFormPath, current: false},
        {name: 'Respostas', href: `${formsPath}/${idform}/replies`, current: true},
    ];

    const loadReplies = useCallback(() => {
        dispatch(resetReplies());
        setReplies([]);
        setExpandedReplyId(null);
        const query = new URLSearchParams({
            page: String(Math.max(0, currentPage - 1)),
            size: String(rowsPerPage),
            search: debouncedSearch,
        });
        fetchReplies(`${baseUrl}?${query.toString()}`, {}, dispatch).catch(() => undefined);
    }, [baseUrl, currentPage, debouncedSearch, dispatch, rowsPerPage]);

    const loadWebhookActivity = useCallback(async () => {
        try {
            const token = sessionStorage.getItem('jwt');
            const headers = {Authorization: `Bearer ${token || ''}`};
            const [webhookResponse, syncResponse] = await Promise.all([
                axios.get(`${URL}/callback/forms/${idform}`, {headers}),
                axios.get(`${URL}/api/forms/${idform}/sync/latest`, {headers}),
            ]);
            setLastWebhookAt(webhookResponse.data?.lastWebhookAt ? new Date(webhookResponse.data.lastWebhookAt) : null);
            if (syncResponse.status === 200 && syncResponse.data?.generatedAt) {
                const report = syncResponse.data;
                const mappedReport = {
                    total: Number(report.totalReferences || 0),
                    validated: Number(report.validatedReferences || 0),
                    notValidated: Number(report.unvalidatedReferences || 0),
                    updated: Number(report.updatedReferences || 0),
                    aligned: Number(report.alignedReferences ?? Math.max(0,
                        Number(report.validatedReferences || 0) - Number(report.updatedReferences || 0))),
                    failed: Number(report.failedReferences || 0),
                    environment: report.environment,
                    generatedAt: report.generatedAt,
                };
                if (window.localStorage.getItem(dismissedSyncStorageKey) !== report.generatedAt) {
                    setSyncReport(mappedReport);
                }
                setLastReferenceSyncAt(new Date(report.generatedAt));
            }
        } catch {
            setLastWebhookAt(null);
        }
    }, [dismissedSyncStorageKey, idform]);

    useEffect(() => {
        loadReplies();
        void loadWebhookActivity();
    }, [loadReplies, loadWebhookActivity]);

    const applyRepliesData = useCallback((payload) => {
        const receivedReplies = Array.isArray(payload?.content) ? payload.content : [];
        setReplies(receivedReplies.filter(Boolean));
        setTotalCount(Number(payload?.totalCount || 0));
        setTotalPages(Math.max(1, Number(payload?.totalPages || 0)));
        setPaidCount(Number(payload?.paidCount || 0));
        setPendingCount(Number(payload?.pendingCount || 0));
    }, []);

    useEffect(() => {
        if (data) applyRepliesData(data);
    }, [data, applyRepliesData]);

    useEffect(() => {
        const refreshSilently = async () => {
            try {
                const token = sessionStorage.getItem('jwt');
                const query = new URLSearchParams({
                    page: String(Math.max(0, currentPage - 1)),
                    size: String(rowsPerPage),
                    search: debouncedSearch,
                });
                const response = await axios.get(`${baseUrl}?${query.toString()}`, {
                    headers: {Authorization: `Bearer ${token || ''}`},
                });
                applyRepliesData(response.data);
                void loadWebhookActivity();
            } catch {
                // Mantém os dados visíveis e deixa a próxima atualização da lista tentar novamente.
            }
        };

        window.addEventListener('focus', refreshSilently);
        return () => window.removeEventListener('focus', refreshSilently);
    }, [applyRepliesData, baseUrl, currentPage, debouncedSearch, loadWebhookActivity, rowsPerPage]);

    useEffect(() => {
        setRowsPerPage(window.innerHeight <= 600 ? 5 : 10);
    }, []);

    useEffect(() => {
        const timer = window.setTimeout(() => setDebouncedSearch(searchTerm.trim()), 350);
        return () => window.clearTimeout(timer);
    }, [searchTerm]);

    const safeCurrentPage = Math.min(currentPage, totalPages);
    const currentRows = replies;
    const pageNumbers = useMemo(() => {
        const numbers = new Set([1, totalPages]);
        for (let page = safeCurrentPage - 2; page <= safeCurrentPage + 2; page += 1) {
            if (page >= 1 && page <= totalPages) numbers.add(page);
        }
        return [...numbers].sort((a, b) => a - b);
    }, [safeCurrentPage, totalPages]);

    const formatDate = (timestamp) => {
        if (!timestamp) return '—';
        const raw = String(timestamp).trim();
        const knownFormats = [
            'yyyy-MM-dd HH:mm:ss',
            'dd:MM:yyyy HH:mm:ss',
            'dd:MM:yyyy HH:mm',
            'dd/MM/yyyy HH:mm',
        ];

        for (const pattern of knownFormats) {
            const candidate = parse(raw, pattern, new Date());
            if (isValid(candidate)) return format(candidate, 'dd/MM/yyyy · HH:mm');
        }

        const isoCandidate = parseISO(raw);
        return isValid(isoCandidate) ? format(isoCandidate, 'dd/MM/yyyy · HH:mm') : raw;
    };

    function timestampToMillis(timestamp) {
        if (!timestamp) return 0;

        const raw = String(timestamp).trim();
        const knownFormats = [
            'yyyy-MM-dd HH:mm:ss',
            'dd:MM:yyyy HH:mm:ss',
            'dd:MM:yyyy HH:mm',
            'dd/MM/yyyy HH:mm',
        ];

        for (const pattern of knownFormats) {
            const candidate = parse(raw, pattern, new Date());
            if (isValid(candidate)) return candidate.getTime();
        }

        const isoCandidate = parseISO(raw);
        return isValid(isoCandidate) ? isoCandidate.getTime() : 0;
    }

    const paymentLabel = (method) => ({
        MULTIBANCO: 'Multibanco',
        MBWAY: 'MB WAY',
        CREDIT_CARD: 'Cartão de crédito',
        GOOGLE_PAY: 'Google Pay',
        APPLE_PAY: 'Apple Pay',
        CASH_PAYMENT: 'Numerário',
    }[method] || method || '—');

    const normalizePaymentMethod = (reference) =>
        String(reference?.referencePaymentMethod || '').trim().toUpperCase();

    const displayReferenceValue = (value) => {
        if (value === null || value === undefined || value === '') return '-';
        if (Number(value) === 0) return '-';
        return value;
    };

    const referenceNumber = (reference) => {
        if (normalizePaymentMethod(reference) === 'CASH_PAYMENT') return '-';
        return displayReferenceValue(reference?.referenceNumber?.number ?? reference?.number);
    };

    const referenceEntity = (reference) => {
        const method = normalizePaymentMethod(reference);
        if (method === 'MBWAY' || method === 'CASH_PAYMENT') return '-';
        return displayReferenceValue(reference?.referenceEntity?.entity ?? reference?.entity);
    };

    const statusLabel = (status) => ({
        PAID: 'Pago',
        UNPAID: 'Pendente',
        CANCELLED: 'Cancelado',
        ERROR: 'Erro',
        EXPIRED: 'Expirado',
        REFUNDED: 'Reembolsado',
    }[status] || status || '—');

    const statusFieldClass = (status) => ({
        PAID: 'reply-status--paid',
        UNPAID: 'reply-status--pending',
        CANCELLED: 'reply-status--cancelled',
        ERROR: 'reply-status--error',
        EXPIRED: 'reply-status--expired',
        REFUNDED: 'reply-status--refunded',
    }[status] || 'reply-status--unknown');

    const statusAccentClass = (status) => ({
        PAID: 'text-emerald-600',
        UNPAID: 'text-orange-500',
        CANCELLED: 'text-red-500',
        ERROR: 'text-rose-600',
        EXPIRED: 'text-amber-600',
        REFUNDED: 'text-blue-500',
    }[status] || 'text-slate-500');

    const money = (value) => `${Number(value || 0).toFixed(2).replace('.', ',')} €`;

    const visibleItems = (items) => Object.entries(items || {})
        .filter(([, quantity]) => Number(quantity) > 0);

    const displayAnswer = (answer) => {
        if (answer === null || answer === undefined || String(answer).trim() === '') return 'Não preenchido';
        return String(answer);
    };

    const handleCopy = async (id) => {
        try {
            await navigator.clipboard.writeText(id);
        } catch (copyError) {
            console.error('Não foi possível copiar o identificador:', copyError);
        }
    };

    const toggleReplyDetails = async (item) => {
        if (expandedReplyId === item.id) {
            setExpandedReplyId(null);
            return;
        }
        if (item.detailsLoaded) {
            setExpandedReplyId(item.id);
            return;
        }

        setLoadingReplyId(item.id);
        try {
            const token = sessionStorage.getItem('jwt');
            const response = await fetch(`${baseUrl}/${item.id}`, {
                headers: {
                    Accept: 'application/json',
                    Authorization: `Bearer ${token || ''}`,
                },
            });
            if (!response.ok) throw new Error(await response.text());
            const details = await response.json();
            setReplies((previous) => previous.map((reply) =>
                reply.id === item.id ? {...reply, ...details, detailsLoaded: true} : reply,
            ));
            setExpandedReplyId(item.id);
        } catch (detailsError) {
            setErrorMessage(detailsError?.message || 'Não foi possível carregar os detalhes da resposta.');
            setTimeout(() => setErrorMessage(''), 5000);
        } finally {
            setLoadingReplyId(null);
        }
    };

    const handleStatusChange = (reference, newStatus) => {
        if (!reference?.referenceIdToString || !newStatus || newStatus === reference.referenceStatus) return;
        setStatusChange({
            referenceID: reference.referenceIdToString,
            previousStatus: reference.referenceStatus,
            newStatus,
        });
        setStatusChangeReason('');
    };

    const closeStatusChange = () => {
        if (!submittingStatus) {
            setStatusChange(null);
            setStatusChangeReason('');
        }
    };

    const confirmStatusChange = async (event) => {
        event.preventDefault();
        if (!statusChange || statusChangeReason.trim().length < 3) return;
        setSubmittingStatus(true);
        setErrorMessage('');
        try {
            const token = sessionStorage.getItem('jwt');
            const response = await fetch(`${URL}/api/reference/${statusChange.referenceID}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    Authorization: `Bearer ${token || ''}`,
                },
                body: JSON.stringify({status: statusChange.newStatus, reason: statusChangeReason.trim()}),
            });
            if (!response.ok) throw new Error(await response.text());
            setSuccessMessage(
                `Estado alterado de ${statusLabel(statusChange.previousStatus)} para ${statusLabel(statusChange.newStatus)}.`,
            );
            setStatusChange(null);
            setStatusChangeReason('');
            loadReplies();
            setTimeout(() => setSuccessMessage(''), 6000);
        } catch (statusError) {
            setErrorMessage(statusError?.message || 'Não foi possível alterar o estado do pagamento.');
            setTimeout(() => setErrorMessage(''), 6000);
        } finally {
            setSubmittingStatus(false);
        }
    };

    const downloadCSV = async (formID) => {
        const token = sessionStorage.getItem('jwt');
        try {
            const response = await axios.get(`${URL}/api/export-csv/form/${formID}/answers`, {
                headers: {Accept: 'text/csv', Authorization: `Bearer ${token}`},
                responseType: 'blob',
            });
            const blobUrl = window.URL.createObjectURL(new Blob([response.data], {type: 'text/csv'}));
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = `respostas-${formID}.csv`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (downloadError) {
            setErrorMessage('Não foi possível exportar o ficheiro CSV.');
            setTimeout(() => setErrorMessage(''), 5000);
        }
    };

    const syncForm = async (formID) => {
        const token = sessionStorage.getItem('jwt');
        const response = await axios.post(`${URL}/api/forms/${formID}/sync`, {}, {
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                Authorization: `Bearer ${token}`,
            },
        });
        return response.status === 200 ? response.data : null;
    };

    const handlePaymentSync = async () => {
        setSyncingPayments(true);
        try {
            const result = await syncForm(idform);
            if (!result) throw new Error('Empty sync response');
            const total = Number(result.totalReferences || 0);
            const validated = Number(result.validatedReferences || 0);
            const unvalidated = Number(result.unvalidatedReferences || 0);
            const updated = Number(result.updatedReferences || 0);
            const aligned = Number(result.alignedReferences ?? Math.max(0, validated - updated));
            const failed = Number(result.failedReferences || 0);

            const generatedAt = result.generatedAt ? new Date(result.generatedAt) : new Date();
            window.localStorage.removeItem(dismissedSyncStorageKey);
            setSyncReport({
                total,
                validated,
                notValidated: unvalidated,
                updated,
                aligned,
                failed,
                environment: result.environment,
                generatedAt: generatedAt.toISOString(),
            });
            setLastReferenceSyncAt(generatedAt);
            setSuccessMessage('Atualização de referências concluída.');
            setTimeout(() => setSuccessMessage(''), 5000);
            loadReplies();
        } catch (syncError) {
            const apiMessage = syncError?.response?.data;
            setErrorMessage(
                typeof apiMessage === 'string' && apiMessage
                    ? apiMessage
                    : 'Não foi possível verificar os pagamentos. Confirma as credenciais da Eupago na tua conta.',
            );
            setTimeout(() => setErrorMessage(''), 5000);
        } finally {
            setSyncingPayments(false);
        }
    };

    const openRefund = (reference, availableAmount) => {
        setRefundReference({...reference, availableRefundAmount: availableAmount});
        setRefundAmount(Number(availableAmount || 0).toFixed(2));
        setRefundReason('');
        setRefundIban('');
        setRefundBic('');
    };

    const closeRefund = () => {
        if (!submittingRefund) setRefundReference(null);
    };

    const handleRefundSubmit = async (event) => {
        event.preventDefault();
        const referenceID = refundReference?.referenceIdToString;
        const amount = Number(String(refundAmount).replace(',', '.'));
        if (!referenceID || !Number.isFinite(amount) || amount <= 0) {
            setErrorMessage('Introduz um valor de reembolso válido.');
            return;
        }

        setSubmittingRefund(true);
        try {
            const token = sessionStorage.getItem('jwt');
            const response = await axios.post(`${URL}/api/reference/${referenceID}/refund`, {
                amount,
                reason: refundReason,
                iban: refundIban,
                bic: refundBic,
            }, {
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });

            setRefundReference(null);
            setSuccessMessage(`Pedido de reembolso enviado${response?.data?.refundId ? ` (ID ${response.data.refundId})` : ''}.`);
            loadReplies();
            setTimeout(() => setSuccessMessage(''), 6000);
        } catch (refundError) {
            const apiMessage = refundError?.response?.data;
            setErrorMessage(
                typeof apiMessage === 'string' && apiMessage
                    ? apiMessage
                    : 'Não foi possível enviar o pedido de reembolso.',
            );
            setTimeout(() => setErrorMessage(''), 6000);
        } finally {
            setSubmittingRefund(false);
        }
    };

    if (loading) return <Spinner/>;

    return (
        <main className="min-h-screen bg-slate-50 px-4 py-6 sm:px-6 lg:px-10">
            <div className="mx-auto max-w-[1600px]">
                <Breadcrumb pages={pages}/>

                <section className="mt-6 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
                    <div className="border-b border-slate-200 px-5 py-6 sm:px-8">
                        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
                            <div>
                                <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700">Gestão de
                                    dados</p>
                                <h1 className="mt-2 text-3xl font-bold tracking-tight !text-slate-950 sm:text-4xl">Respostas
                                    recebidas</h1>
                                <p className="mt-2 text-sm !text-slate-600">Abre cada resposta para consultar os campos
                                    preenchidos e os itens correspondentes.</p>
                            </div>

                            <div className="flex flex-wrap items-stretch gap-2">
                                <button type="button" onClick={() => downloadCSV(idform)}
                                        className="inline-flex h-10 shrink-0 self-center items-center gap-1.5 rounded-xl border border-slate-300 bg-white px-3.5 text-xs font-semibold !text-slate-700 shadow-sm transition hover:border-slate-400 hover:bg-slate-50">
                                    <ArrowDownTrayIcon className="h-4 w-4"/> Exportar CSV
                                </button>
                                <div className="inline-flex min-w-[265px] items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-emerald-900">
                                    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-white text-emerald-700 shadow-sm">
                                        <ArrowPathIcon className="h-5 w-5"/>
                                    </span>
                                    <div>
                                        <p className="text-sm font-bold">Sincronização automática ativa</p>
                                        <p className="mt-0.5 text-xs text-emerald-700">
                                            {lastWebhookAt
                                                ? `Última notificação do webhook: ${lastWebhookAt.toLocaleString('pt-PT')}`
                                                : 'Webhook ativo · ainda sem notificações recebidas'}
                                        </p>
                                        {lastReferenceSyncAt && (
                                            <p className="mt-0.5 text-xs font-semibold text-emerald-800">
                                                Última consulta manual: {lastReferenceSyncAt.toLocaleString('pt-PT')}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <button type="button" onClick={handlePaymentSync}
                                        disabled={syncingPayments}
                                        title={adminMode
                                            ? 'Usa as credenciais e o ambiente do canal responsável por este formulário.'
                                            : 'Consulta o ambiente associado à Chave API do formulário.'}
                                        className="inline-flex items-center gap-2 rounded-xl bg-green-cl px-4 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60">
                                    <ArrowPathIcon className={`h-5 w-5 ${syncingPayments ? 'animate-spin' : ''}`}/>
                                    {syncingPayments
                                        ? 'A verificar…'
                                        : 'Atualização de referências'}
                                </button>
                            </div>
                        </div>

                        <div className="mt-6 grid gap-3 sm:grid-cols-3">
                            {[
                                [totalCount, 'Total de respostas'],
                                [paidCount, 'Pagamentos concluídos'],
                                [pendingCount, 'Pagamentos pendentes'],
                            ].map(([value, label]) => (
                                <div key={label} className="rounded-2xl border border-slate-200 bg-slate-50 px-5 py-4">
                                    <p className="text-2xl font-bold !text-slate-950">{value}</p>
                                    <p className="mt-1 text-sm !text-slate-600">{label}</p>
                                </div>
                            ))}
                        </div>

                        {syncReport && (
                            <PaymentAuditReport report={syncReport} onClose={() => {
                                window.localStorage.setItem(dismissedSyncStorageKey, syncReport.generatedAt);
                                setSyncReport(null);
                            }} />
                        )}
                    </div>

                    <div className="p-5 sm:p-8">
                        <div className="relative">
                            <MagnifyingGlassIcon
                                className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400"/>
                            <input
                                id="reply-search"
                                type="search"
                                value={searchTerm}
                                onChange={(event) => {
                                    setSearchTerm(event.target.value);
                                    setCurrentPage(1);
                                }}
                                placeholder="Procurar por identificador, estado, item ou resposta"
                                className="block w-full rounded-xl border border-slate-300 bg-white py-3 pl-11 pr-4 text-sm !text-slate-900 placeholder:text-slate-400 focus:border-emerald-700 focus:ring-emerald-700"
                            />
                        </div>

                        {error && (
                            <div role="alert"
                                 className="mt-5 flex flex-col gap-3 rounded-2xl border border-red-200 bg-red-50 p-5 sm:flex-row sm:items-center sm:justify-between">
                                <div>
                                    <p className="font-semibold !text-red-900">Não foi possível carregar as
                                        respostas.</p>
                                    <p className="mt-1 text-sm !text-red-700">{String(error)}</p>
                                </div>
                                <button type="button" onClick={loadReplies}
                                        className="rounded-lg bg-red-700 px-4 py-2 text-sm font-semibold text-white">Tentar
                                    novamente
                                </button>
                            </div>
                        )}

                        {!error && currentRows.length === 0 ? (
                            <div
                                className="mt-5 rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-6 py-14 text-center">
                                <InboxArrowDownIcon className="mx-auto h-12 w-12 text-emerald-700"/>
                                <h2 className="mt-4 text-xl font-bold !text-slate-950">{searchTerm ? 'Nenhuma resposta encontrada' : 'Ainda não existem respostas'}</h2>
                                <p className="mx-auto mt-2 max-w-xl text-sm leading-6 !text-slate-600">{searchTerm ? 'Altera ou limpa a pesquisa.' : 'As respostas aparecem aqui depois de um cliente concluir o formulário público.'}</p>
                            </div>
                        ) : !error && (
                            <div className="mt-5 space-y-3">
                                {currentRows.map((item) => {
                                    const reference = item?.reference || {};
                                    const isExpanded = expandedReplyId === item.id;
                                    const fields = item?.replyFields || [];
                                    const items = visibleItems(item?.itemsPurchasedAndQuantity);
                                    const notes = Array.isArray(item?.notes) ? item.notes : [];
                                    const refundRequests = notes.filter(note => note?.type === 'PENDING_REFUND_REQUEST');
                                    const refundNotifications = notes.filter(note => note?.type === 'REFUND_NOTIFICATION');
                                    const refundPending = refundRequests.length > refundNotifications.length;
                                    const confirmedRefundAmount = refundNotifications.reduce(
                                        (total, note) => total + (Number(note?.amount) || 0),
                                        0,
                                    );
                                    const availableRefundAmount = Math.max(
                                        0,
                                        Number(reference?.amount || 0) - confirmedRefundAmount,
                                    );

                                    return (
                                        <article key={item.id}
                                                 className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                                            <div
                                                className="grid gap-4 p-4 lg:grid-cols-[minmax(240px,1.4fr)_repeat(3,minmax(120px,0.7fr))_minmax(190px,0.9fr)_minmax(210px,auto)] lg:items-end">
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <button type="button" onClick={() => handleCopy(item.id)}
                                                                aria-label="Copiar identificador"
                                                                className="rounded-lg border border-slate-200 p-2 text-slate-600 hover:bg-slate-100">
                                                            <DocumentDuplicateIcon className="h-4 w-4"/>
                                                        </button>
                                                        <span className="truncate font-mono text-xs !text-slate-700"
                                                              title={item.id}>{item.id}</span>
                                                    </div>
                                                    <p className="mt-2 text-sm !text-slate-600">{formatDate(item.timestamp)}</p>
                                                </div>

                                                <div><p
                                                    className="text-xs font-bold uppercase tracking-wide !text-slate-500">Método</p>
                                                    <p className="mt-1 text-sm font-semibold !text-slate-800">{paymentLabel(reference.referencePaymentMethod)}</p>
                                                </div>
                                                <div><p
                                                    className="text-xs font-bold uppercase tracking-wide !text-slate-500">Valor</p>
                                                    <p className="mt-1 text-sm font-semibold !text-slate-950">{money(reference.amount)}</p>
                                                </div>
                                                <div>
                                                    <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide !text-slate-500">
                                                        <ShoppingBagIcon className="h-4 w-4" aria-hidden="true"/>
                                                        Itens
                                                    </p>
                                                    <p className="mt-1 text-sm font-semibold !text-slate-800">{items.length}</p>
                                                </div>
                                                <div className="flex min-w-0 flex-col gap-2">
                                                    <label
                                                        htmlFor={`status-${item.id}`}
                                                        className="min-h-[18px] text-xs font-bold uppercase leading-[18px] tracking-wide !text-slate-500"
                                                    >
                                                        Estado
                                                    </label>

                                                    <div className="relative">
                                                        <select
                                                            id={`status-${item.id}`}
                                                            value={reference.referenceStatus || ''}
                                                            disabled={!reference.referenceIdToString || reference.referenceStatus === 'REFUNDED'}
                                                            onChange={(event) => handleStatusChange(reference, event.target.value)}
                                                            className={`reply-status block h-11 w-full appearance-none border py-0 pl-6 pr-11 text-center text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 ${statusFieldClass(reference.referenceStatus)}`}
                                                        >
                                                            {!reference.referenceStatus &&
                                                                <option className="bg-white text-slate-900"
                                                                        value="">—</option>}
                                                            {referenceStatusOptions.map((status) => (
                                                                <option className="bg-white text-slate-900" key={status}
                                                                        value={status} disabled={status === 'REFUNDED'}>
                                                                    {statusLabel(status)}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        <ChevronDownIcon
                                                            aria-hidden="true"
                                                            className={`pointer-events-none absolute right-5 top-1/2 h-5 w-5 -translate-y-1/2 ${statusAccentClass(reference.referenceStatus)}`}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="flex min-w-[210px] flex-col gap-2">
                          <span
                              aria-hidden="true"
                              className="min-h-[18px] text-xs font-bold uppercase leading-[18px] tracking-wide text-transparent"
                          >
                            Ações
                          </span>

                                                    <button
                                                        type="button"
                                                        onClick={() => toggleReplyDetails(item)}
                                                        disabled={loadingReplyId === item.id}
                                                        aria-expanded={isExpanded}
                                                        aria-controls={`reply-details-${item.id}`}
                                                        className="inline-flex h-11 w-full items-center justify-between gap-3 rounded-xl border border-slate-300 bg-white px-4 text-sm font-semibold !text-slate-700 shadow-sm transition hover:border-slate-400 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
                                                    >
                                                        <span>{loadingReplyId === item.id ? 'A carregar…' : isExpanded ? 'Ocultar informação' : 'Ver informação'}</span>
                                                        <ChevronDownIcon
                                                            className={`h-5 w-5 shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                                        />
                                                    </button>
                                                </div>
                                            </div>

                                            {isExpanded && (
                                                <div id={`reply-details-${item.id}`}
                                                     className="border-t border-slate-200 bg-slate-50 p-4 sm:p-6">
                                                    <div className="grid gap-6 xl:grid-cols-2">
                                                        <section
                                                            className="rounded-2xl border border-sky-100 bg-sky-50/50 p-5 xl:col-span-2">
                                                            <div className="flex items-center justify-between gap-3">
                                                                <h2 className="text-lg font-bold !text-slate-950">Dados
                                                                    da referência</h2>
                                                                <span
                                                                    className="rounded-full bg-white px-3 py-1 text-xs font-semibold !text-sky-800">Pagamento</span>
                                                            </div>
                                                            <dl className="mt-4 grid gap-3 sm:grid-cols-3">
                                                                <div
                                                                    className="rounded-xl border border-sky-100 bg-white px-4 py-3">
                                                                    <dt className="text-xs font-bold uppercase tracking-wide !text-slate-500">Entidade</dt>
                                                                    <dd className="m-0 mt-1 font-mono text-sm font-semibold !text-slate-900">{referenceEntity(reference)}</dd>
                                                                </div>
                                                                <div
                                                                    className="rounded-xl border border-sky-100 bg-white px-4 py-3">
                                                                    <dt className="text-xs font-bold uppercase tracking-wide !text-slate-500">Referência</dt>
                                                                    <dd className="m-0 mt-1 font-mono text-sm font-semibold !text-slate-900">{referenceNumber(reference)}</dd>
                                                                </div>
                                                                <div
                                                                    className="rounded-xl border border-sky-100 bg-white px-4 py-3">
                                                                    <dt className="text-xs font-bold uppercase tracking-wide !text-slate-500">Email
                                                                        para notificações
                                                                    </dt>
                                                                    <dd className="m-0 mt-1 break-all text-sm font-semibold !text-slate-900">{item?.notificationEmail || '—'}</dd>
                                                                </div>
                                                            </dl>
                                                        </section>

                                                        <section
                                                            className="rounded-2xl border border-slate-200 bg-white p-5">
                                                            <div className="flex items-center justify-between gap-3">
                                                                <h2 className="text-lg font-bold !text-slate-950">Campos
                                                                    preenchidos</h2>
                                                                <span
                                                                    className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold !text-slate-600">{fields.length}</span>
                                                            </div>
                                                            {fields.length === 0 ? (
                                                                <p className="mt-4 text-sm !text-slate-600">Não existem
                                                                    campos associados a esta resposta.</p>
                                                            ) : (
                                                                <dl className="mt-4 divide-y divide-slate-100">
                                                                    {fields.map((field, index) => (
                                                                        <div
                                                                            key={`${field?.formElementId || field?.label || 'field'}-${index}`}
                                                                            className="grid gap-1 py-3 sm:grid-cols-[minmax(150px,0.8fr)_1.4fr] sm:gap-5">
                                                                            <dt className="text-sm font-semibold !text-slate-700">{field?.label || 'Campo'}</dt>
                                                                            <dd className="m-0 whitespace-pre-wrap break-words text-sm !text-slate-900">{displayAnswer(field?.answer)}</dd>
                                                                        </div>
                                                                    ))}
                                                                </dl>
                                                            )}
                                                        </section>

                                                        <section
                                                            className="rounded-2xl border border-slate-200 bg-white p-5">
                                                            <div className="flex items-center justify-between gap-3">
                                                                <h2 className="flex items-center gap-2 text-lg font-bold !text-slate-950">
                                                                    <ShoppingBagIcon className="h-6 w-6"
                                                                                     aria-hidden="true"/>
                                                                    Itens correspondentes
                                                                </h2>
                                                                <span
                                                                    className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold !text-slate-600">{items.length}</span>
                                                            </div>
                                                            {items.length === 0 ? (
                                                                <p className="mt-4 text-sm !text-slate-600">Não existem
                                                                    itens associados a esta resposta.</p>
                                                            ) : (
                                                                <ul className="mt-4 space-y-2">
                                                                    {items.map(([label, quantity], index) => {
                                                                        const colors = itemColorPalette[index % itemColorPalette.length];
                                                                        return (
                                                                            <li key={label}
                                                                                className={`flex items-center justify-between gap-5 rounded-xl border px-4 py-3 ${colors.row}`}>
                                        <span className="flex min-w-0 items-center gap-3">
                                          <span aria-hidden="true"
                                                className={`h-2 w-2 shrink-0 rounded-full ${colors.marker}`}/>
                                          <span className="truncate text-sm font-semibold !text-slate-800"
                                                title={label}>{label}</span>
                                        </span>
                                                                                <span
                                                                                    className={`min-w-9 rounded-lg px-3 py-1 text-center text-sm font-bold ${colors.quantity}`}>{quantity}</span>
                                                                            </li>
                                                                        );
                                                                    })}
                                                                </ul>
                                                            )}
                                                        </section>

                                                        {notes.length > 0 && (
                                                            <section
                                                                className="rounded-2xl border border-amber-200 bg-amber-50/60 p-5 xl:col-span-2">
                                                                <div
                                                                    className="flex items-center justify-between gap-3">
                                                                    <h2 className="text-lg font-bold !text-slate-950">Notas
                                                                        do pagamento</h2>
                                                                    <span
                                                                        className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold !text-amber-800">{notes.length}</span>
                                                                </div>
                                                                <ul className="mt-4 space-y-2">
                                                                    {notes.map((note, index) => (
                                                                        <li key={`${note?.externalId || note?.createdAt || 'note'}-${index}`}
                                                                            className="rounded-xl border border-amber-200 bg-white px-4 py-3">
                                                                            <div
                                                                                className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                                                                                <p className="text-sm font-semibold !text-slate-900">
                                                                                    {note?.type === 'PENDING_REFUND_REQUEST'
                                                                                        ? 'Pedido de reembolso pendente'
                                                                                        : note?.type === 'MANUAL_STATUS_CHANGE'
                                                                                            ? 'Alteração manual de estado'
                                                                                            : 'Notificação de reembolso'}
                                                                                </p>
                                                                                <p className="text-xs !text-slate-500">
                                                                                    {note?.createdAt ? formatDate(note.createdAt) : ''}
                                                                                </p>
                                                                            </div>
                                                                            <p className="mt-1 text-sm !text-slate-700">{note?.message}</p>
                                                                            {note?.amount != null && (
                                                                                <p className="mt-2 text-xs font-semibold !text-amber-800">Valor: {money(note.amount)}</p>
                                                                            )}
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            </section>
                                                        )}
                                                    </div>

                                                    <div className="mt-4 text-xs !text-slate-500">IP
                                                        registado: {item.ip || '—'}</div>
                                                    {reference.referenceStatus === 'PAID'
                                                        && reference.referencePaymentMethod !== 'CASH_PAYMENT'
                                                        && reference.referenceIdToString && (
                                                            <div
                                                                className="mt-5 flex justify-end border-t border-slate-200 pt-4">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => openRefund(reference, availableRefundAmount)}
                                                                    disabled={refundPending || availableRefundAmount <= 0}
                                                                    className="rounded-xl border border-red-200 bg-white px-4 py-2.5 text-sm font-semibold !text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:border-slate-200 disabled:!text-slate-500 disabled:hover:bg-white"
                                                                >
                                                                    {refundPending
                                                                        ? 'Reembolso em processamento'
                                                                        : availableRefundAmount <= 0
                                                                            ? 'Valor totalmente reembolsado'
                                                                            : `Pedir reembolso · ${money(availableRefundAmount)} disponível`}
                                                                </button>
                                                            </div>
                                                        )}
                                                </div>
                                            )}
                                        </article>
                                    );
                                })}
                            </div>
                        )}

                        {totalPages > 1 && (
                            <nav aria-label="Paginação" className="mt-5 flex justify-end gap-1">
                                {pageNumbers.map((number) => (
                                    <button key={number} type="button" onClick={() => setCurrentPage(number)}
                                            aria-current={safeCurrentPage === number ? 'page' : undefined}
                                            className={`min-w-9 rounded-lg px-3 py-2 text-sm font-semibold ${safeCurrentPage === number ? 'bg-green-cl text-white' : 'border border-slate-300 !text-slate-700'}`}>
                                        {number}
                                    </button>
                                ))}
                            </nav>
                        )}
                    </div>
                </section>

                {errorMessage && <div
                    className="fixed bottom-5 right-5 z-50 max-w-md rounded-xl bg-red-700 p-4 text-sm text-white shadow-xl"
                    role="alert">{errorMessage}</div>}
                {successMessage && <div
                    className="fixed bottom-5 right-5 z-50 max-w-md rounded-xl bg-emerald-700 p-4 text-sm text-white shadow-xl"
                    role="status">{successMessage}</div>}

                {statusChange && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4"
                         role="dialog" aria-modal="true" aria-labelledby="status-change-title">
                        <form onSubmit={confirmStatusChange}
                              className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
                            <h2 id="status-change-title" className="text-2xl font-bold !text-slate-950">Confirmar alteração de estado</h2>
                            <p className="mt-2 text-sm leading-6 !text-slate-600">
                                Vais alterar manualmente o pagamento de <strong>{statusLabel(statusChange.previousStatus)}</strong> para <strong>{statusLabel(statusChange.newStatus)}</strong>. Esta operação pode alterar o stock e ficará registada no histórico.
                            </p>
                            <div className="mt-5">
                                <label htmlFor="statusChangeReason" className="text-sm font-semibold !text-slate-800">Motivo da alteração *</label>
                                <textarea id="statusChangeReason" value={statusChangeReason} maxLength={500} required
                                          onChange={(event) => setStatusChangeReason(event.target.value)}
                                          placeholder="Ex.: confirmação recebida por suporte"
                                          className="mt-2 block min-h-28 w-full rounded-xl border border-slate-300 p-3 !text-slate-900"/>
                                <p className="mt-1 text-xs !text-slate-500">Mínimo de 3 caracteres. O motivo ficará associado à referência.</p>
                            </div>
                            <div className="mt-6 flex justify-end gap-2">
                                <button type="button" onClick={closeStatusChange} disabled={submittingStatus}
                                        className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold !text-slate-700 disabled:opacity-50">Cancelar</button>
                                <button type="submit" disabled={submittingStatus || statusChangeReason.trim().length < 3}
                                        className="rounded-xl bg-green-cl px-4 py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60">
                                    {submittingStatus ? 'A guardar…' : 'Confirmar alteração'}
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {refundReference && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4"
                         role="dialog" aria-modal="true" aria-labelledby="refund-title">
                        <form onSubmit={handleRefundSubmit}
                              className="w-full max-w-xl rounded-2xl bg-white p-6 shadow-2xl">
                            <h2 id="refund-title" className="text-2xl font-bold !text-slate-950">Pedir reembolso</h2>
                            <p className="mt-2 text-sm !text-slate-600">O pedido será enviado de forma segura para a
                                Eupago e ficará registado como nota pendente. O estado do pagamento original não será
                                alterado.</p>
                            <div
                                className="mt-4 grid grid-cols-2 gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm">
                                <div>
                                    <p className="!text-slate-500">Pagamento original</p>
                                    <p className="mt-1 font-bold !text-slate-900">{money(refundReference.amount)}</p>
                                </div>
                                <div>
                                    <p className="!text-slate-500">Disponível para reembolso</p>
                                    <p className="mt-1 font-bold !text-emerald-700">{money(refundReference.availableRefundAmount)}</p>
                                </div>
                            </div>

                            <div className="mt-6 grid gap-4 sm:grid-cols-2">
                                <div>
                                    <label htmlFor="refundAmount" className="text-sm font-semibold !text-slate-800">Valor
                                        (€)</label>
                                    <input id="refundAmount" inputMode="decimal" min="0.01"
                                           max={refundReference.availableRefundAmount} step="0.01" value={refundAmount}
                                           onChange={(event) => setRefundAmount(event.target.value)} required
                                           className="mt-2 block w-full rounded-xl border border-slate-300 p-3 !text-slate-900"/>
                                </div>
                                <div>
                                    <label htmlFor="refundReason"
                                           className="text-sm font-semibold !text-slate-800">Motivo</label>
                                    <input id="refundReason" value={refundReason}
                                           onChange={(event) => setRefundReason(event.target.value)}
                                           placeholder="Opcional"
                                           className="mt-2 block w-full rounded-xl border border-slate-300 p-3 !text-slate-900"/>
                                </div>
                                <div>
                                    <label htmlFor="refundIban"
                                           className="text-sm font-semibold !text-slate-800">IBAN</label>
                                    <input id="refundIban" value={refundIban}
                                           onChange={(event) => setRefundIban(event.target.value)}
                                           placeholder="Opcional para MB WAY e cartão"
                                           className="mt-2 block w-full rounded-xl border border-slate-300 p-3 !text-slate-900"/>
                                </div>
                                <div>
                                    <label htmlFor="refundBic"
                                           className="text-sm font-semibold !text-slate-800">BIC/SWIFT</label>
                                    <input id="refundBic" value={refundBic}
                                           onChange={(event) => setRefundBic(event.target.value)} placeholder="Opcional"
                                           className="mt-2 block w-full rounded-xl border border-slate-300 p-3 !text-slate-900"/>
                                </div>
                            </div>

                            <div className="mt-6 flex justify-end gap-2">
                                <button type="button" onClick={closeRefund} disabled={submittingRefund}
                                        className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold !text-slate-700 disabled:opacity-50">Cancelar
                                </button>
                                <button type="submit" disabled={submittingRefund}
                                        className="rounded-xl bg-red-700 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-60">
                                    {submittingRefund ? 'A enviar…' : 'Confirmar pedido'}
                                </button>
                            </div>
                        </form>
                    </div>
                )}
            </div>
        </main>
    );
};

function PaymentAuditReport({report, onClose}) {
    const hasIssues = Number(report.notValidated || 0) > 0 || Number(report.failed || 0) > 0;
    const generatedAt = report.generatedAt
        ? new Date(report.generatedAt).toLocaleString('pt-PT')
        : '—';

    return (
        <div className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50/80 p-5">
            <div className="flex items-start justify-between gap-4">
                <div className="flex min-w-0 items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-emerald-700 shadow-sm">
                        <ClipboardDocumentCheckIcon className="h-6 w-6" />
                    </span>
                    <div>
                        <h2 className="font-bold !text-slate-900">Resultado da atualização de referências</h2>
                        <p className="mt-1 text-sm !text-emerald-800">
                            Consulta manual concluída em {generatedAt}. Os estados continuam a ser atualizados automaticamente pelas notificações da Eupago.
                        </p>
                        {report.environment && (
                            <span className="mt-2 inline-flex rounded-full border border-emerald-200 bg-white px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-emerald-800">
                                Ambiente: {report.environment === 'SANDBOX' ? 'Sandbox' : report.environment === 'PRODUCTION' ? 'Produção' : 'Misto'}
                            </span>
                        )}
                    </div>
                </div>
                <button type="button" onClick={onClose} aria-label="Fechar relatório"
                        className="rounded-lg p-1.5 text-emerald-800 hover:bg-emerald-100">
                    <XMarkIcon className="h-5 w-5" />
                </button>
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
                <ReportValue value={report.validated} label="Validadas na Eupago" />
                <ReportValue value={report.notValidated} label="Não validadas" />
                <ReportValue value={report.updated} label="Estados atualizados" />
                <ReportValue value={report.aligned} label="Estados já alinhados" />
                <ReportValue value={report.failed} label="Erros de validação" danger={Number(report.failed || 0) > 0} />
            </div>

            {hasIssues && (
                <p className="mt-4 text-sm font-semibold !text-amber-800">
                    Algumas referências não foram validadas. Confirma os dados, as credenciais e o canal antes de repetir a verificação.
                </p>
            )}
        </div>
    );
}

function ReportValue({value, label, danger = false}) {
    return (
        <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
            <p className={`text-xl font-bold ${danger ? '!text-red-600' : '!text-slate-950'}`}>{Number(value || 0)}</p>
            <p className="mt-1 text-xs !text-slate-500">{label}</p>
        </div>
    );
}

export default Replies;
